import type { KeyboardEvent, KeyboardEventHandler } from 'react';

type CodeValueSetter = (value: string) => void;
type CodeEditorKeyDown = KeyboardEventHandler<HTMLDivElement> & KeyboardEventHandler<HTMLTextAreaElement>;

function getLineIndent(value: string, position: number): string {
  const lineStart = value.lastIndexOf('\n', Math.max(0, position - 1)) + 1;
  const line = value.slice(lineStart, position);
  return line.match(/^\s*/)?.[0] ?? '';
}

function placeCaret(textarea: HTMLTextAreaElement, position: number) {
  window.requestAnimationFrame(() => {
    textarea.selectionStart = position;
    textarea.selectionEnd = position;
  });
}

export function createCodeEditorKeyDown(
  value: string,
  setValue: CodeValueSetter,
): CodeEditorKeyDown {
  return ((event: KeyboardEvent<HTMLDivElement> | KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;

    if (!(event.currentTarget instanceof HTMLTextAreaElement)) return;

    const textarea = event.currentTarget;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const indent = getLineIndent(value, start);

    if (event.key === '{') {
      event.preventDefault();
      const selected = value.slice(start, end);
      const insert = selected
        ? `{\n${indent}  ${selected}\n${indent}}`
        : `{\n${indent}  \n${indent}}`;
      const nextValue = before + insert + after;
      const caretPosition = selected
        ? start + 2 + indent.length + 2 + selected.length
        : start + 2 + indent.length + 2;
      setValue(nextValue);
      placeCaret(textarea, caretPosition);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const previousChar = before.slice(-1);
      const nextChar = after.charAt(0);
      const shouldExpandBlock = previousChar === '{' && nextChar === '}';
      const innerIndent = previousChar === '{' ? `${indent}  ` : indent;
      const insert = shouldExpandBlock
        ? `\n${innerIndent}\n${indent}`
        : `\n${innerIndent}`;
      const nextValue = before + insert + after;
      setValue(nextValue);
      placeCaret(textarea, start + 1 + innerIndent.length);
    }
  }) as CodeEditorKeyDown;
}
